import { z } from 'zod';
import { ROLES } from './rbac.js';
import { CONNECTOR_CERTIFICATIONS, ERP_OPERATIONS, erpConnectorKeySchema, HOMOLOGATION_STEPS } from './erp.js';
import { erpConnectorDefinitionSchema } from './erp-definition.js';
import { AGENT_TOOLS, AI_PROVIDERS } from './agents.js';
import { RESTARTABLE_SERVICES } from './instance.js';
import { modelFallbackSchema, modelIssueSchema, sealedCredentialSchema, sealedErpCredentialsSchema } from './ai.js';

/* ───────────── utilitários ───────────── */

export function onlyDigits(v: string): string {
  return v.replace(/\D/g, '');
}

/** Validação de CNPJ pelos dígitos verificadores. */
export function isValidCnpj(value: string): boolean {
  const d = onlyDigits(value);
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
  const calc = (len: number) => {
    const weights = len === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((acc, w, i) => acc + w * Number(d[i]), 0);
    const r = sum % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return calc(12) === Number(d[12]) && calc(13) === Number(d[13]);
}

/** Rejeita endereços que não podem ser o IP público de uma VPS (loopback, privados, link-local, CGNAT, multicast). */
export function isPublicIpv4(ip: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip);
  if (!m) return false;
  const o = m.slice(1).map(Number) as [number, number, number, number];
  if (o.some((n) => n > 255)) return false;
  const [a, b] = o;
  if (a === 0 || a === 10 || a === 127 || a >= 224) return false;
  if (a === 169 && b === 254) return false;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  return true;
}

const hostnameRegex = /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const BLOCKED_HOST_SUFFIXES = ['.local', '.localhost', '.internal', '.lan', '.home.arpa'];

export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(hostnameRegex, 'Informe um domínio válido, por exemplo atendimento.provedor.com.br')
  .refine((h) => !BLOCKED_HOST_SUFFIXES.some((s) => h.endsWith(s)), 'Domínio interno não é permitido');

/* ───────────── autenticação ───────────── */

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email('E-mail inválido').max(254),
  password: z.string().min(1, 'Informe a senha').max(256),
});

export const totpCodeSchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, 'O código tem 6 dígitos'),
});

export const passwordPolicy = z
  .string()
  .min(12, 'Mínimo de 12 caracteres')
  .max(256)
  .refine((p) => /[a-zA-Z]/.test(p) && /\d/.test(p), 'Use letras e números');

export const adminUserCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.string().trim().toLowerCase().email().max(254),
  role: z.enum(ROLES),
  temporaryPassword: passwordPolicy,
});

export const adminUserUpdateSchema = z.object({
  role: z.enum(ROLES).optional(),
  disabled: z.boolean().optional(),
});

/* ───────────── provedores ───────────── */

export const CONTRACT_STATUSES = ['onboarding', 'active', 'suspended', 'terminated'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  onboarding: 'Implantação',
  active: 'Ativo',
  suspended: 'Suspenso',
  terminated: 'Encerrado',
};

export const providerCreateSchema = z.object({
  legalName: z.string().trim().min(3, 'Informe a razão social').max(200),
  tradeName: z.string().trim().min(2, 'Informe o nome fantasia').max(120),
  document: z
    .string()
    .trim()
    .refine(isValidCnpj, 'CNPJ inválido')
    .transform(onlyDigits),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]([a-z0-9-]{1,38}[a-z0-9])$/, 'Use letras minúsculas, números e hífen (3–40)'),
  contactName: z.string().trim().min(2, 'Informe o responsável').max(120),
  contactEmail: z.string().trim().toLowerCase().email('E-mail inválido').max(254),
  contactPhone: z
    .string()
    .trim()
    .transform(onlyDigits)
    .refine((v) => v === '' || (v.length >= 10 && v.length <= 13), 'Telefone inválido')
    .optional(),
  city: z.string().trim().max(120).optional(),
  state: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'UF inválida')
    .optional()
    .or(z.literal('')),
  subscriberBase: z.coerce.number().int().min(0).max(10_000_000).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ProviderCreateInput = z.input<typeof providerCreateSchema>;

export const providerUpdateSchema = providerCreateSchema
  .omit({ slug: true, document: true })
  .partial()
  .extend({ contractStatus: z.enum(CONTRACT_STATUSES).optional() });

/* ───────────── instâncias ───────────── */

export const instanceRegisterSchema = z.object({
  hostingProvider: z.string().trim().min(2).max(80),
  region: z.string().trim().min(2).max(80),
  publicIp: z.string().trim().refine(isPublicIpv4, 'Informe um IPv4 público'),
  domain: hostnameSchema,
  sshHostKeyFingerprint: z
    .string()
    .trim()
    .regex(/^SHA256:[A-Za-z0-9+/]{43}=?$/, 'Formato esperado: SHA256:… (ssh-keygen -lf)')
    .optional()
    .or(z.literal('')),
  vcpu: z.coerce.number().int().min(1).max(256),
  memoryGb: z.coerce.number().int().min(1).max(2048),
  diskGb: z.coerce.number().int().min(10).max(100_000),
});

export const instanceOperationSchema = z.discriminatedUnion('operation', [
  z.object({ operation: z.literal('restart_service'), service: z.enum(RESTARTABLE_SERVICES), reason: z.string().trim().min(5).max(500) }),
  z.object({ operation: z.literal('run_backup'), reason: z.string().trim().min(5).max(500) }),
  z.object({ operation: z.literal('verify_backup'), reason: z.string().trim().min(5).max(500) }),
  z.object({ operation: z.literal('set_ai_credentials'), credential: sealedCredentialSchema, reason: z.string().trim().min(5).max(500) }),
  z.object({ operation: z.literal('set_erp_credentials'), credential: sealedErpCredentialsSchema, reason: z.string().trim().min(5).max(500) }),
  z.object({
    operation: z.literal('test_erp_connection'),
    /** Operação a testar; sem ela, só verifica comunicação e autenticação. */
    erpOperation: z.enum(ERP_OPERATIONS).optional(),
    /** Valores do AMBIENTE DE TESTE (ex.: CPF de cliente fictício). O resultado nunca traz dados. */
    variables: z.record(z.string().regex(/^[a-z_][a-z0-9_]{0,39}$/), z.string().max(200)).default({}),
    reason: z.string().trim().min(5).max(500),
  }),
  z.object({ operation: z.literal('collect_diagnostics'), reason: z.string().trim().min(5).max(500) }),
  z.object({
    operation: z.literal('apply_release'),
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    reason: z.string().trim().min(5).max(500),
  }),
]);

export const provisioningStepUpdateSchema = z.object({
  status: z.enum(['pending', 'done', 'failed', 'skipped']),
  note: z.string().trim().max(1000).optional(),
});

/* ───────────── ingestão (instância → master) ───────────── */

export const heartbeatSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  cpuPct: z.number().min(0).max(100),
  memoryPct: z.number().min(0).max(100),
  diskPct: z.number().min(0).max(100),
  containers: z
    .array(
      z.object({
        name: z.string().max(64),
        state: z.enum(['running', 'restarting', 'exited', 'paused', 'created', 'dead']),
        health: z.enum(['healthy', 'unhealthy', 'starting', 'none']),
      }),
    )
    .max(50),
  backup: z
    .object({
      lastSuccessAt: z.string().datetime().nullable(),
      lastStatus: z.enum(['success', 'failed', 'running', 'never']),
      lastVerifiedAt: z.string().datetime().nullable().optional(),
      lastVerifyStatus: z.enum(['success', 'failed', 'never']).optional(),
      count: z.number().int().min(0).optional(),
    })
    .optional(),
  /** Chave pública RSA (SPKI, PEM) da instância, para cifrar credenciais no navegador. */
  publicKeyPem: z.string().max(2000).startsWith('-----BEGIN PUBLIC KEY-----').optional(),
  /** Hash da configuração do motor aplicada na instância. */
  engineConfigVersion: z.string().max(80).nullable().optional(),
  /** Credenciais de IA configuradas (somente indicação, nunca o valor). */
  aiCredentials: z.array(z.enum(AI_PROVIDERS)).max(5).optional(),
  /** Credenciais do ERP configuradas (somente indicação, nunca o valor). */
  erpCredentials: z.object({ connector: z.string().max(40), updatedAt: z.string().max(40) }).nullable().optional(),
  /** Modelos disponíveis por fornecedor, consultados pela instância com a própria chave. */
  aiModels: z.record(z.enum(AI_PROVIDERS), z.array(z.string().max(80)).max(300)).optional(),
  /** Substituições automáticas recentes (modelo retirado pelo fornecedor). */
  modelIssues: z.array(modelIssueSchema).max(20).optional(),
  /** Falhas de IA da última hora, agrupadas por fornecedor/modelo/tipo (sem dados de clientes). */
  aiErrors: z
    .array(
      z.object({
        aiProvider: z.string().max(20),
        model: z.string().max(80),
        kind: z.string().max(30),
        count: z.number().int().min(0),
        lastAt: z.string(),
        lastMessage: z.string().max(400),
      }),
    )
    .max(10)
    .optional(),
  checks: z
    .object({
      apiHealthy: z.boolean(),
      database: z.boolean(),
      https: z.boolean().nullable(),
      secrets: z.boolean(),
      internalPortsClosed: z.boolean(),
      adminUserExists: z.boolean(),
    })
    .partial()
    .optional(),
  whatsappConnected: z.boolean().optional(),
  erpHealthy: z.boolean().nullable().optional(),
  conversationsLast24h: z.number().int().min(0).optional(),
});
export type HeartbeatInput = z.infer<typeof heartbeatSchema>;

export const usageIngestSchema = z.object({
  records: z
    .array(
      z.object({
        day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        aiProvider: z.enum(AI_PROVIDERS),
        model: z.string().min(1).max(80),
        profileKey: z.string().min(1).max(40),
        requests: z.number().int().min(0),
        inputTokens: z.number().int().min(0),
        outputTokens: z.number().int().min(0),
        costUsdMicros: z.number().int().min(0),
        latencyMsP50: z.number().int().min(0),
        latencyMsP95: z.number().int().min(0),
        errors: z.number().int().min(0),
        conversations: z.number().int().min(0).default(0),
      }),
    )
    .min(1)
    .max(500),
});

/* ───────────── releases ───────────── */

const imageRef = z
  .string()
  .trim()
  // registro[:porta]/caminho[:tag][@sha256:digest]
  .regex(/^[a-z0-9]+([._-][a-z0-9]+)*(:\d{1,5})?(\/[a-z0-9]+([._-][a-z0-9]+)*)+(:\w[\w.-]{0,127})?(@sha256:[a-f0-9]{64})?$/, 'Referência de imagem inválida');

export const releaseCreateSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/, 'Use versionamento semântico (1.2.3)'),
  images: z.object({ api: imageRef, web: imageRef, agent: imageRef }),
  notes: z.string().trim().max(5000).optional(),
});
export type ReleaseManifest = z.infer<typeof releaseCreateSchema>;

export function isPinnedImage(ref: string): boolean {
  return /@sha256:[a-f0-9]{64}$/.test(ref);
}

/* ───────────── agentes ───────────── */

/**
 * Inteligência artificial usada por TODOS os perfis (fornecedor, modelo, reservas). A chave de
 * cada provedor de internet fica na instância dele — é por ela que o consumo é separado.
 */
export const aiSettingsSchema = z.object({
  aiProvider: z.enum(AI_PROVIDERS),
  model: z.string().trim().min(2, 'Informe o modelo').max(80),
  fallbacks: z.array(modelFallbackSchema).max(3).default([]),
  temperature: z.coerce.number().min(0).max(1),
  maxOutputTokens: z.coerce.number().int().min(64).max(8_192),
  /** Espera por mensagens "picadas": responde só depois de N segundos sem mensagem nova (0 = desliga). */
  groupWindowSeconds: z.coerce.number().int().min(0).max(20).default(4),
});
export type AiSettings = z.infer<typeof aiSettingsSchema>;

export const agentProfileCreateSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(60),
  purpose: z.string().trim().min(10, 'Explique quando a Triagem deve encaminhar para este perfil').max(400),
});
export const agentProfileUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(60),
    purpose: z.string().trim().min(10).max(400),
    archived: z.boolean(),
  })
  .partial();

/** Versão de um perfil: só o comportamento. O modelo de IA vem da configuração geral. */
export const agentVersionCreateSchema = z.object({
  instructions: z.string().trim().min(20, 'Instruções muito curtas').max(30_000),
  responsePolicy: z.string().trim().max(5_000).default(''),
  handoffRules: z.string().trim().max(5_000).default(''),
  tools: z.array(z.enum(AGENT_TOOLS)).max(AGENT_TOOLS.length),
  maxToolCallsPerTurn: z.coerce.number().int().min(0).max(10),
  changeNote: z.string().trim().min(3, 'Descreva a alteração').max(500),
});
export type AgentVersionInput = z.input<typeof agentVersionCreateSchema>;

/* ───────────── integrações e consumo ───────────── */

export const providerIntegrationSchema = z.object({
  connector: erpConnectorKeySchema,
  enabledOperations: z.array(z.enum(ERP_OPERATIONS)).max(ERP_OPERATIONS.length),
});

const erpOperationsSchema = z.array(z.enum(ERP_OPERATIONS)).max(ERP_OPERATIONS.length).transform((v) => [...new Set(v)]);

export const erpConnectorCreateSchema = z.object({
  key: erpConnectorKeySchema,
  name: z.string().trim().min(2, 'Informe o nome').max(80),
  vendor: z.string().trim().min(2, 'Informe o fabricante').max(80),
  notes: z.string().trim().max(1000).default(''),
  supportedOperations: erpOperationsSchema.default([]),
});

export const erpConnectorUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(80),
    vendor: z.string().trim().min(2).max(80),
    notes: z.string().trim().max(1000),
    supportedOperations: erpOperationsSchema,
    checklist: z.array(z.enum(HOMOLOGATION_STEPS)).transform((v) => [...new Set(v)]),
    certification: z.enum(CONNECTOR_CERTIFICATIONS),
    definition: erpConnectorDefinitionSchema,
  })
  .partial();

export const erpPreviewSchema = z.object({
  operation: z.enum(ERP_OPERATIONS),
  definition: erpConnectorDefinitionSchema,
  /** Resposta de exemplo do ERP (texto JSON colado da documentação ou do ambiente de teste). */
  sample: z.string().max(500_000),
  variables: z.record(z.string().max(60), z.string().max(200)).default({}),
});

export const budgetSchema = z.object({
  monthlyLimitUsd: z.coerce.number().min(0).max(1_000_000),
  alertThresholdPct: z.coerce.number().int().min(10).max(100),
  hardLimit: z.boolean(),
});

export const usageQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  providerId: z.string().uuid().optional(),
});

export const auditQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  action: z.string().trim().max(80).optional(),
  providerId: z.string().uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
